import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useMutation, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { LightbulbIcon, X } from 'lucide-react-native';
import { useCallback, useEffect, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { useColors } from './useColors';

import Feedback from '@/app/(secure)/feedback/[id]';
import FeedbackSuccess from '@/app/(secure)/feedback/success';
import type { HeaderItem, HeaderMenuAction } from '@/components/headerItems/types';
import { Alert, AlertIcon } from '@/components/ui/alert';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { getCloseInquiryCopy } from '@/constants/closeInquiryCopy';
import { useGroupedInquiriesEnabled } from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { useInquiryClose } from '@/hooks/inbox/useInquiryClose';
import { useIsSmallDevice } from '@/hooks/useIsSmallDevice';
import { useToaster } from '@/hooks/useToaster';
import type { ChatConversation } from '@/services/chat/chatTransport';
import { isReadOnlyConversation } from '@/services/chat/readOnlyInquiryConversation';
import type { GetInquiriesV2Response, InquiryV2 } from '@/services/getInquiriesV2';
import { getInquiryV2QueryOptions, type GetInquiryV2Response } from '@/services/getInquiryV2';
import { updateInquiryV2 } from '@/services/updateInquiryV2';
import { segmentClient } from '@/util/analytics';
import { trackInquiryClosed } from '@/util/analyticsEvents';
import { invalidateUnreadInquiriesDebounced } from '@/util/inboxInvalidation';
import { isAskAnonymouslyRoutedInquiry } from '@/util/inquiryDisplay';

const CLOSE_INQUIRY_MODAL_COPY = {
  title: 'Close this inquiry?',
  cta: 'Close & rate inquiry',
} as const;

export const useCloseConversation = ({
  conversation,
  userId,
  recipientId,
  recipientName,
  conversationStatus,
  inquiryFeedbackStatus,
  authorId,
  inquiry,
  isAskAnonymouslyRelayView = false,
}: {
  conversation: ChatConversation;
  userId: string;
  recipientId: string;
  recipientName?: string;
  conversationStatus: InquiryV2['inquiryStatus'];
  inquiryFeedbackStatus: InquiryV2['feedbackStatus'];
  authorId: number;
  inquiry: Pick<InquiryV2, 'id' | 'parentId' | 'inquiryGroupSize' | 'anonymous' | 'recipient'>;
  /**
   * True when Ask Anonymously is the recipient, so the press marks the inquiry handled. Ignored
   * for the inquiry author, whose press is a real close and keeps the standard wording.
   */
  isAskAnonymouslyRelayView?: boolean;
}) => {
  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const isSmallDevice = useIsSmallDevice();

  const isAuthor = authorId === Number(userId);

  /**
   * Primum Support reading an inquiry it is not part of, through the read-only
   * conversation. Closing or archiving belongs to the inquiry's participants,
   * so the reader gets neither the header action nor the close banners.
   */
  const isObserver = isReadOnlyConversation(conversation);

  const { colors } = useColors();

  const { navigate } = useRouter();

  const queryClient = useQueryClient();

  const {
    state: closeState,
    isFeedbackActive,
    startFeedback,
    completeFeedback,
    reset: resetCloseState,
    dispatch,
  } = useInquiryClose();

  const isGrouped = (inquiry.inquiryGroupSize ?? 1) > 1;

  const isAnonymous = inquiry.anonymous === true;

  const inquiryLabel = isAnonymous ? 'anonymous inquiry' : 'inquiry';

  const closeModalCopy = {
    title: recipientName
      ? `Close this ${inquiryLabel} with ${recipientName}?`
      : CLOSE_INQUIRY_MODAL_COPY.title,
    description: isGrouped
      ? `This will close your ${inquiryLabel} with ${recipientName} only. If you want to send a message after closing, please submit a new inquiry.`
      : `This will close your ${inquiryLabel}${recipientName ? ` with ${recipientName}` : ''}. If you want to send a message after closing, please submit a new inquiry.`,
    cta: CLOSE_INQUIRY_MODAL_COPY.cta,
  };

  /**
   * Every inquiry in the Ask Anonymously inbox gets the handled wording, not only anonymous
   * ones. An inquiry submitted with no expert selected lands in the same inbox and the press
   * does the same thing there, but the relay view is scoped to anonymous inquiries and missed it.
   */
  const isAskAnonymouslyRecipient =
    !isAuthor && (isAskAnonymouslyRelayView || isAskAnonymouslyRoutedInquiry(inquiry));

  const closeCopy = getCloseInquiryCopy({ isAuthor, isAskAnonymouslyRecipient });

  const closeButtonLabel = isSmallDevice ? closeCopy.buttonMobile : closeCopy.buttonDesktop;

  const [showBanner, setShowBanner] = useState<
    'requestToClose' | 'pendingToClose' | 'closeAsAuthor' | 'rateNow' | undefined
  >(undefined);

  const [feedbackStatus, setFeedbackStatus] = useState<'inProgress' | 'success' | undefined>(
    undefined
  );

  const [isOpeningFeedback, setIsOpeningFeedback] = useState(false);

  const [feedbackConversationId, setFeedbackConversationId] = useState<string | undefined>(
    undefined
  );

  const [feedbackRating, setFeedbackRating] = useState<string | undefined>(undefined);

  /**
   * Transition to feedback phase in the global close state.
   * This gates navigation advancement until feedback completes.
   * Uses reducer dispatch for explicit state transitions.
   */
  const transitionToFeedback = useCallback(() => {
    startFeedback();
  }, [startFeedback]);

  /**
   * Mark feedback as complete, allowing advancement to proceed.
   * Uses reducer dispatch for explicit state transitions.
   */
  const transitionToFeedbackComplete = useCallback(() => {
    completeFeedback();
  }, [completeFeedback]);

  const openFeedbackForConversation = useCallback(
    (conversationId: string) => {
      setFeedbackConversationId(conversationId);

      setFeedbackStatus('inProgress');

      transitionToFeedback();
    },
    [transitionToFeedback]
  );

  const inquiryClosedContext = {
    inquiryId: inquiry.id,
    inquiryParentId: inquiry.parentId ?? inquiry.id,
    inquiryGroupSize: inquiry.inquiryGroupSize ?? 1,
    isGroupedInquiry: (inquiry.inquiryGroupSize ?? 1) > 1,
    inquiryGroupsEnabled: isInboxGroupedInquiriesEnabled,
  };

  const closeAndOpenFeedback = async () => {
    setIsOpeningFeedback(true);

    try {
      await inquiryUpdateMutationV2.mutateAsync({
        conversationId: conversation.id,
        state: 'closed',
      });

      trackInquiryClosed(conversation.id, inquiryClosedContext);

      openFeedbackForConversation(conversation.id);
    } finally {
      setIsOpeningFeedback(false);
    }
  };

  const closeAndRateConversation = async ({
    shouldClearBanner,
  }: {
    shouldClearBanner: boolean;
  }) => {
    dispatch({ type: 'INITIATE', conversationId: conversation.id });

    dispatch({ type: 'CONFIRM' });

    if (isWeb) {
      await closeAndOpenFeedback();

      return;
    }

    await inquiryUpdateMutationV2.mutateAsync({
      conversationId: conversation.id,
      state: 'closed',
    });

    trackInquiryClosed(conversation.id, inquiryClosedContext);

    if (shouldClearBanner) {
      setShowBanner(undefined);
    }

    transitionToFeedback();

    navigate(`/feedback/${conversation.id}`);
  };

  useEffect(() => {
    if (conversationStatus === 'inactive' && !isAuthor) {
      return setShowBanner('requestToClose');
    }

    if (conversationStatus === 'inactive' && isAuthor) {
      return setShowBanner('pendingToClose');
    }

    if (conversationStatus === 'closed' && inquiryFeedbackStatus !== 'Submitted' && isAuthor) {
      return setShowBanner('rateNow');
    }

    setShowBanner(undefined);
  }, [conversationStatus, isAuthor, inquiryFeedbackStatus]);

  const toast = useToaster();

  /**
   * Cache invalidation after inquiry state mutations.
   * Invalidates: single inquiry, inquiry list with refetchType active, unread counts debounced.
   */
  const handleInquiryMutationSettled = (
    _data: unknown,
    _error: unknown,
    variables: { conversationId: string }
  ) => {
    queryClient
      .invalidateQueries({
        queryKey: getInquiryV2QueryOptions({ conversationId: variables.conversationId }).queryKey,
        refetchType: 'active',
      })
      .catch(() => undefined);

    queryClient
      .invalidateQueries({
        queryKey: ['inquiriesV2'],
        refetchType: 'active',
      })
      .catch(() => undefined);

    invalidateUnreadInquiriesDebounced(queryClient);
  };

  /**
   * Mutation to update inquiry status: active, inactive, or closed.
   * Optimistically updates both single-inquiry and list caches for immediate UI feedback.
   * Chat composer enable/disable is driven by inquiryStatus, so optimistic updates prevent
   * the composer from staying disabled after "Continue conversation" clicks.
   */
  const inquiryUpdateMutationV2 = useMutation({
    mutationFn: updateInquiryV2,
    onMutate: async (variables) => {
      // This mutation only ever drives status changes, so it always carries a
      // state; the optimistic status writes below have nothing to do without one.
      // Captured into a const so the narrowing holds inside the updater closures.
      const nextStatus = variables.state;

      if (!nextStatus) {
        return;
      }

      queryClient.setQueryData<GetInquiryV2Response>(
        getInquiryV2QueryOptions({ conversationId: variables.conversationId }).queryKey,
        (prev) => (prev ? { ...prev, inquiryStatus: nextStatus } : prev)
      );

      queryClient.setQueriesData<InfiniteData<GetInquiriesV2Response>>(
        {
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'inquiriesV2' &&
            query.queryKey.length >= 4,
        },
        (prev) => {
          if (!prev?.pages) {
            return prev;
          }

          return {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              inquiries: page.inquiries?.map((inquiry) => {
                if (inquiry.conversationId === variables.conversationId) {
                  return { ...inquiry, inquiryStatus: nextStatus };
                }

                return inquiry;
              }),
            })),
          };
        }
      );
    },
    onError: (error, variables) => {
      const title =
        (variables.state &&
          {
            closed: 'Unabled to close inquiry',
            inactive: 'Unable to request to close inquiry',
            active: 'Unable to cancel request to close Inquiry',
          }[variables.state]) ||
        'Unable to complete action';

      toast.showError({
        title,
      });
    },
    onSettled: handleInquiryMutationSettled,
  });

  const closeDisabled = inquiryUpdateMutationV2.isPending || !!showBanner;

  const onClosePress = () => {
    if (!isAuthor) {
      inquiryUpdateMutationV2.mutateAsync({
        conversationId: conversation.id,
        state: 'inactive',
      });
    }

    segmentClient.track(ANALYTICS_EVENTS.INQUIRY_REQUEST_CLOSE, {
      inquiry_id: conversation.id,
    });

    setShowBanner(isAuthor ? 'closeAsAuthor' : 'requestToClose');
  };

  const headerRightAction = () => (
    <Button
      size="sm"
      variant="link"
      action="primary"
      disabled={closeDisabled}
      className={twJoin(closeDisabled ? 'opacity-50' : '', 'web:mr-5')}
      onPressIn={onClosePress}>
      <ButtonText>{closeButtonLabel}</ButtonText>
    </Button>
  );

  const requestToCloseBanner = (
    <Alert action="muted" className="flex rounded-none">
      <VStack space="md" className="items-start">
        <Heading size="h2-md" className="text-typography-900">
          {closeCopy.pendingTitle}
        </Heading>

        <Text size="sm" className="text-typography-700">
          {closeCopy.pendingBody}
        </Text>

        <Button
          disabled={inquiryUpdateMutationV2.isPending}
          size="xs"
          variant="outline"
          action="default"
          className="gap-x-2"
          onPress={async () => {
            await inquiryUpdateMutationV2.mutateAsync({
              conversationId: conversation.id,
              state: 'active',
            });

            setShowBanner(undefined);
          }}>
          {inquiryUpdateMutationV2.isPending &&
          inquiryUpdateMutationV2.variables.state === 'active' ? (
            <ButtonSpinner color={colors.secondary[200]} />
          ) : null}

          <ButtonText>{closeCopy.pendingAction}</ButtonText>
        </Button>
      </VStack>
    </Alert>
  );

  const readyToCloseBanner = (
    <Alert action="muted">
      <VStack className="w-full flex-1">
        <Heading size="h2-md" className="mb-1 text-typography-900">
          Are you ready to close this {inquiryLabel}?
        </Heading>

        <Text size="sm" className="mb-3 text-typography-700">
          {isGrouped
            ? `This will close your ${inquiryLabel} with ${recipientName} only. If you want to send a message after closing, please submit a new inquiry.`
            : `This will close your ${inquiryLabel}${recipientName ? ` with ${recipientName}` : ''}. If you want to send a message after closing, please submit a new inquiry.`}
        </Text>

        <HStack space="lg">
          <Button
            disabled={inquiryUpdateMutationV2.isPending}
            size="xs"
            variant="outline"
            action="default"
            className="gap-x-2"
            onPress={async () => {
              if (conversationStatus === 'inactive') {
                await inquiryUpdateMutationV2.mutateAsync({
                  conversationId: conversation.id,
                  state: 'active',
                });
              }

              segmentClient.track(ANALYTICS_EVENTS.INQUIRY_CANCEL_CLOSE, {
                inquiry_id: conversation.id,
              });

              setShowBanner(undefined);
            }}>
            {inquiryUpdateMutationV2.isPending &&
            inquiryUpdateMutationV2.variables.state === 'active' ? (
              <ButtonSpinner color={colors.secondary[200]} />
            ) : null}

            <ButtonText>Continue conversation</ButtonText>
          </Button>

          <Button
            size="xs"
            action="secondary"
            className={twJoin(
              'gap-x-2',
              isWeb &&
                'data-[hover=true]:border-secondary-300 data-[hover=true]:bg-secondary-500 data-[active=true]:border-secondary-300 data-[active=true]:bg-secondary-500'
            )}
            disabled={inquiryUpdateMutationV2.isPending}
            onPress={async () => {
              await closeAndRateConversation({ shouldClearBanner: true });
            }}>
            {inquiryUpdateMutationV2.isPending &&
            inquiryUpdateMutationV2.variables.state === 'closed' ? (
              <ButtonSpinner color={colors.secondary[200]} />
            ) : null}

            <ButtonText className="text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0">
              Close & rate
            </ButtonText>
          </Button>
        </HStack>
      </VStack>
    </Alert>
  );

  const modalClose = (
    <Modal
      isOpen={showBanner === 'closeAsAuthor'}
      onClose={() => setShowBanner(undefined)}
      size="md">
      <ModalBackdrop />

      <ModalContent className="max-h-[70vh] gap-4 p-5" testID="close-inquiry-modal">
        <ModalHeader>
          <Heading size="lg">{closeModalCopy.title}</Heading>

          <ModalCloseButton>
            <Icon as={X} size="md" />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <Text size="md" className="leading-relaxed text-typography-700">
            {closeModalCopy.description}
          </Text>
        </ModalBody>

        <ModalFooter className="flex-col gap-2">
          <Button
            testID="close-and-rate-button"
            action="secondary"
            size="lg"
            disabled={inquiryUpdateMutationV2.isPending}
            className={twJoin(
              'w-full gap-x-2 rounded-lg',
              isWeb &&
                'data-[hover=true]:border-secondary-300 data-[hover=true]:bg-secondary-500 data-[active=true]:border-secondary-300 data-[active=true]:bg-secondary-500'
            )}
            onPress={async () => {
              await closeAndRateConversation({ shouldClearBanner: false });
            }}>
            {inquiryUpdateMutationV2.isPending ? (
              <ButtonSpinner color={colors.secondary[200]} />
            ) : null}

            <ButtonText className="text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0">
              {closeModalCopy.cta}
            </ButtonText>
          </Button>

          <Button
            variant="link"
            action="secondary"
            size="lg"
            onPress={() => setShowBanner(undefined)}>
            <ButtonText size="sm" className="text-typography-500">
              Cancel
            </ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const rateNowBanner = (
    <Alert action="info" className="flex flex-row items-start gap-4 rounded-none">
      <AlertIcon as={LightbulbIcon} />

      <VStack space="md" className="flex-1 items-start">
        <Heading size="h2-md" className="text-secondary-500">
          Help us help you
        </Heading>

        <Text size="sm">Primum uses your reviews to increase efficacy and improve our product</Text>

        <Button
          size="xs"
          action="secondary"
          onPress={async () => {
            if (isWeb) {
              return openFeedbackForConversation(conversation.id);
            }

            navigate(`/feedback/${conversation.id}`);
          }}>
          <ButtonText className="text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0">
            Rate now
          </ButtonText>
        </Button>
      </VStack>
    </Alert>
  );

  const webInquiryModals = isWeb ? (
    <>
      <Feedback
        conversationId={feedbackConversationId ?? conversation.id}
        isOpen={feedbackStatus === 'inProgress'}
        onClose={() => {
          setFeedbackStatus(undefined);

          setFeedbackConversationId(undefined);

          transitionToFeedbackComplete();
        }}
        onSuccess={(rating) => {
          setFeedbackRating(rating);

          setFeedbackStatus('success');
        }}
      />
      <FeedbackSuccess
        isOpen={feedbackStatus === 'success'}
        onClose={() => {
          setFeedbackStatus(undefined);

          setFeedbackConversationId(undefined);

          transitionToFeedbackComplete();
        }}
        rating={feedbackRating}
      />
    </>
  ) : null;

  const closedConversation = conversationStatus === 'closed';

  /**
   * While the recipient's request-to-close banner is up, the banner's own button is the
   * only action left, so the header button is hidden rather than shown disabled beside it.
   */
  const isAwaitingSubmitter = showBanner === 'requestToClose';

  const localFeedbackFlowOpen = feedbackStatus !== undefined || isOpeningFeedback;

  const resolveTopBanner = () => {
    switch (showBanner) {
      case 'requestToClose':
        return requestToCloseBanner;
      case 'pendingToClose':
        return readyToCloseBanner;
      case 'closeAsAuthor':
        return modalClose;
      case 'rateNow':
        return rateNowBanner;
      default:
        return null;
    }
  };

  const topBanner = isObserver ? null : resolveTopBanner();

  const canClose = !(closedConversation || isAwaitingSubmitter || isObserver);

  /**
   * The close action for a native header's trailing slot. Always an item, hidden when the
   * conversation cannot be closed, so the bar keeps the same children as the inquiry changes
   * state.
   */
  const closeHeaderItem: HeaderItem = {
    type: 'button',
    key: 'close-inquiry',
    label: closeButtonLabel,
    isDisabled: closeDisabled,
    isHidden: !canClose,
    onPress: onClosePress,
  };

  /**
   * The close action inside the inquiry's "…" menu, for a phone's bar. Hidden rather than
   * removed when the conversation cannot be closed, for the same reason as the item above.
   * A menu row has the width the bar does not, so it always takes the full label. Its icon
   * follows the label: a checked speech bubble where a recipient marks the inquiry answered or
   * handled, a cross where the author closes it. Not a checked circle, which the composer's "On
   * your honor" control already wears.
   */
  const closeMenuAction: HeaderMenuAction = {
    key: 'close-inquiry',
    label: closeCopy.buttonDesktop,
    sfIcon: isAuthor ? 'xmark.circle' : 'checkmark.bubble',
    isDisabled: closeDisabled,
    isHidden: !canClose,
    onPress: onClosePress,
  };

  return {
    isAuthor,
    closedConversation,
    isFeedbackActive: isFeedbackActive || localFeedbackFlowOpen,
    closeState,
    headerRightAction: canClose ? headerRightAction : undefined,
    closeHeaderItem,
    closeMenuAction,
    closeDisabled,
    closeButtonLabel,
    onClosePress,
    topBanner,
    webInquiryModals,
    transitionToFeedbackComplete,
    resetCloseState,
  };
};
